/**
 * The browser's half of setting a member's photo (§14.79), shared by the builder (owner functions)
 * and the launcher (member functions): resize locally, POST the bytes to a one-time upload URL
 * from the caller's mutation, hand the `storageId` to the caller's set mutation.
 */
import type { Id } from '@circles/convex';
import { AVATAR_MAX_BYTES, resizeAvatar } from './avatarImage';
import { errorInfo } from './convexError';

/** What the file picker offers; the server accepts exactly these types. */
export const AVATAR_ACCEPT = 'image/jpeg,image/png,image/webp';

/** A user-facing message for anything that can go wrong while picking, uploading or saving a photo. */
export class AvatarError extends Error {}

/** The upload as the caller wires it: its mutations, with its own credentials. */
export type AvatarUploader = {
  getUploadUrl: () => Promise<string>;
  setAvatar: (storageId: Id<'_storage'>) => Promise<unknown>;
};

export async function uploadAvatar(file: File, up: AvatarUploader): Promise<void> {
  let blob: Blob;
  try {
    blob = await resizeAvatar(file);
  } catch {
    throw new AvatarError("That file isn't a photo we can read. Choose a JPEG, PNG or WebP image.");
  }
  if (blob.size > AVATAR_MAX_BYTES) throw new AvatarError('That photo is too large even after shrinking. Try another one.');
  const url = await up.getUploadUrl();
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': blob.type }, body: blob });
  if (!res.ok) throw new AvatarError(`The photo could not be uploaded (${res.status}). Try again.`);
  const json = (await res.json().catch(() => ({}))) as { storageId?: string };
  if (!json.storageId) throw new AvatarError('The photo could not be uploaded. Try again.');
  await up.setAvatar(json.storageId as Id<'_storage'>);
}

/** The message to show: our own, or the server's (INVALID / FORBIDDEN are already plain words). */
export function avatarErrorMessage(err: unknown): string {
  if (err instanceof AvatarError) return err.message;
  const info = errorInfo(err);
  if (info.code === 'INVALID' || info.code === 'FORBIDDEN') return info.message;
  return "Couldn't update the photo. Check your connection and try again.";
}
